import React, {
	Fragment,
	useState,
	useEffect,
	useRef,
	lazy,
	Suspense,
	useMemo,
	useCallback,
} from "react";
import {
	Container,
	ShowDisney,
	useGenericContext,
	AccessibleText,
	BackButton,
	ChromeLinks,
	Profile,
	SearchBar,
} from "@wdpr/groot-components";
import appConfig from "config.json";
import { removeHoverOfSection, toggleBodyClass } from "utils";
import { trackLink } from "utils/analyticsFramework";
import {
	filterDiscoveryAgentLinks,
	resolveDaLogic,
} from "utils/discoveryAgent";
import AnalyticsWrapper from "components/shared/AnalyticsWrapper";
import Logo from "../../Logo";
import Cart from "../section/cart/Cart";
import SectionTxp from "../section/SectionTxp";
import PageHeaderTxp from "./PageHeaderTxp";
import {
	txpMotionClasses,
	txpMotionDelayMs,
	isProfileSection,
	compactLayoutMediaQuery,
} from "config-txp";
import { isNavFloat, getSearchAutocompleteEnginte } from "utils/txp";
import RightNavigation from "./RightNavigation";
import usePreventScroll from "hooks/usePreventScroll";
import useCheckMediaQuery from "hooks/useCheckMediaQuery";
const NestedMenu = lazy(() => import("./NestedMenu"));
const navigationAppMenuLinksId = "navigationAppMenuLinks";
const { brands, focusableElements } = appConfig;
const syndicatedNavigationClass = "syndicated-navigation";
const syndicatedNavigationTxpClass = `${syndicatedNavigationClass}__txp`;
const syndicatedNavigationNavClass = `${syndicatedNavigationClass}__nav`;
const syndicatedNavigationToggleClass = `${syndicatedNavigationNavClass}__toggle`;
const syndicatedNavigationClassHide = `${syndicatedNavigationClass}__hide`;
const syndicatedNavigationClassShow = `${syndicatedNavigationClass}__show`;
const navigationBaseClasses = `${syndicatedNavigationNavClass} ${syndicatedNavigationTxpClass}`;
const largeAmountOfSections = 8;
const preventScrollTxpClass = "navigation-prevent-scroll-txp";
const openSearchClass = "navigation-search-open-txp";

const scrollTopAndLeft = (top = 0, left = 0) => {
	window?.scrollTo({
		top,
		left,
		behavior: "smooth",
	});
};

const NavigationTxp = ({
	showLogo,
	headerRef,
	isHeaderHovered,
	searchBarToggle,
	setSearchBarToggle,
}) => {
	const {
		config: {
			brand,
			chromeLinks,
			locale,
			navigation: showNavigation,
			searchBar,
			showSections,
			store,
			showBackButton,
			isTxp,
			chrome: showChromeItems,
		},
		isMobileView,
		headerData: { navigation, chrome, backButton, extraOptions },
		profileData: {
			openedOneIdModal,
			userType,
			user,
			isLoading: isProfileLoading,
		},
		modal,
		cdnAssetsUrl,
		isScrolling,
		isSticky,
		forceCompactLayout,
		setForceCompactLayout,
	} = useGenericContext();

	const backdropRef = useRef(null);
	const brandConfig = brands[brand] || {};
	const navRef = useRef(null);
	const sectionNavRef = useRef(null);
	const sectionRefs = useRef({});
	const [isMenuActive, setIsMenuActive] = useState(false);
	const [menuToggle, setMenuToggle] = useState(false);
	const [collapsedSection, setCollapsedSection] = useState("");
	const [logoLoaded, setLogoLoaded] = useState(false);
	const [hoveredSection, setHoveredSection] = useState(null);
	const [searchHidden, setSearchHidden] = useState(false);
	const [sectionsHovered, setSectionsHovered] = useState(false);
	const switchTimeoutRef = useRef(null);
	const prevIsHeaderHoveredRef = useRef(isHeaderHovered);
	const menuWasOpenBeforeSearchRef = useRef(false);
	const [backdropExpanded, setBackdropExpanded] = useState(false);

	// Discovery Agent link visibility
	const [showDaLinks, setShowDaLinks] = useState(false);

	useEffect(() => {
		if (!brandConfig.discoveryAgentEnabled) return;
		const customDaLogic = resolveDaLogic(chrome?.customDaLogic);
		if (customDaLogic === "show") {
			setShowDaLinks(true);
		} else if (customDaLogic === "check") {
			if (isProfileLoading) return;
			import("utils/discoveryAgent")
				.then(({ checkDiscoveryAgentAccess }) =>
					checkDiscoveryAgentAccess(user)
				)
				.then(setShowDaLinks);
		}
		// else: stays false (hide)
	}, [
		chrome?.customDaLogic,
		user,
		isProfileLoading,
		brandConfig.discoveryAgentEnabled,
	]);

	const handleSetHoveredSection = useCallback(
		(newId) => {
			if (newId && hoveredSection && newId !== hoveredSection) {
				clearTimeout(switchTimeoutRef.current);
				setHoveredSection(null);
				switchTimeoutRef.current = setTimeout(() => {
					setHoveredSection(newId);
				}, txpMotionDelayMs.fadeOutDuration);
			} else {
				clearTimeout(switchTimeoutRef.current);
				setHoveredSection(newId);
			}
		},
		[hoveredSection]
	);

	const handleSectionsMouseEnter = useCallback(() => {
		setSectionsHovered(true);
	}, []);

	const handleSectionsMouseLeave = useCallback(() => {
		setSectionsHovered(false);
		// Only clear hoveredSection if the mouse has left the header entirely.
		// When still within the header (e.g. moving to logo or right nav), keep
		// the section open — the useEffect watching isHeaderHovered handles cleanup.
		if (!isHeaderHovered) {
			clearTimeout(switchTimeoutRef.current);
			setHoveredSection(null);
		}
	}, [isHeaderHovered]);

	useEffect(() => {
		if (hoveredSection && !isProfileSection(hoveredSection)) {
			setBackdropExpanded(true);
		} else if (!hoveredSection && !sectionsHovered) {
			// Only collapse when mouse truly left the sections area
			setBackdropExpanded(false);
		}
		// When hoveredSection is null but sectionsHovered is true (switching),
		// do nothing — keep backdropExpanded as-is
	}, [hoveredSection, sectionsHovered]);

	const [currentSelectedOption, setCurrentSelectedOption] = useState(null);
	const [nestedMenuOption, setNestedMenuOption] = useState(navigation);
	const [animationClass, setAnimationClass] = useState(
		menuToggle ? txpMotionClasses.motionFadeIn : txpMotionClasses.motionFadeOut
	);
	const [menuItemAnimationClass, setMenuItemAnimationClass] = useState(
		menuToggle
			? `${txpMotionClasses.motionFadeIn} ${txpMotionClasses.motionPositionNavEnterDown}`
			: ""
	);
	const [fade, setFade] = useState("fade-in");
	const excludeAriaRole = brandConfig?.excludeAriaRole || false;
	const hideSearch = chrome?.hideSearch === "true";
	const showSearchBar =
		!store &&
		searchBar &&
		!brandConfig?.hideSearchBarForUserTypes?.includes(userType) &&
		chrome?.searchDisney &&
		!hideSearch;

	const hideMenuToggler = (!showSections && store) || !showNavigation;
	const navigationClasses = useMemo(
		() =>
			`${navigationBaseClasses} ${syndicatedNavigationNavClass}--${brand} ${syndicatedNavigationNavClass}--${brand}--${locale}${
				menuToggle ? ` ${syndicatedNavigationNavClass}--open` : ""
			}${
				forceCompactLayout ? ` ${syndicatedNavigationNavClass}--compact` : ""
			}`,
		[brand, locale, menuToggle, forceCompactLayout]
	);

	const [motionClasses, setMotionClasses] = useState(
		txpMotionClasses.motionFadeOut
	);
	const motionFadeOutTimerRef = useRef(null);

	const handleOnScrimClick = useCallback(() => {
		setSearchBarToggle(false);
	}, [setSearchBarToggle]);

	useEffect(() => {
		const shouldFadeIn =
			isHeaderHovered ||
			searchBarToggle ||
			backdropExpanded ||
			isNavFloat() ||
			(isScrolling && isSticky);
		if (shouldFadeIn) {
			clearTimeout(motionFadeOutTimerRef.current);
			setMotionClasses(txpMotionClasses.motionFadeIn);
		} else {
			motionFadeOutTimerRef.current = setTimeout(() => {
				setMotionClasses(txpMotionClasses.motionFadeOut);
			}, 200);
		}
		return () => clearTimeout(motionFadeOutTimerRef.current);
	}, [
		backdropExpanded,
		isHeaderHovered,
		isSticky,
		isScrolling,
		searchBarToggle,
	]);

	const searchAutocomplete = useMemo(() => {
		const searchEngineParam = getSearchAutocompleteEnginte();
		const search = searchEngineParam || chrome?.searchEngine;

		return search === "autocomplete";
	}, [chrome?.searchEngine]);

	useEffect(() => {
		document?.addEventListener("keydown", handleKeydown, true);
		toggleBodyClass(true, "navigation-nav__txp--backdrop");
		return () => {
			document?.removeEventListener("keydown", handleKeydown, true);
			toggleBodyClass(false, "navigation-nav__txp--backdrop");
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			isMobileView &&
			collapsedSection &&
			sectionRefs.current[collapsedSection]
		) {
			sectionRefs.current[collapsedSection].scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [collapsedSection, isMobileView]);

	const onLogoLoadCallback = () => {
		setLogoLoaded(true);
	};

	const handleCloseMenu = () => {
		document?.body.classList?.remove("navigation-nav-open-txp");
		setMenuToggle(false);
	};

	const handleMenuToggle = () => {
		if (!menuToggle && !isSticky) {
			scrollTopAndLeft();
		}
		document?.body.classList?.toggle("navigation-nav-open-txp");
		setMenuToggle((prevState) => !prevState);
		if (brands[brand]?.menuTracking) {
			trackLink({
				linkId: `${brand}-hamburger-${menuToggle ? "close" : "open"}`,
			});
		}
	};

	const handleCloseIcon = () => {
		handleCloseMenu();
		setTimeout(() => {
			setNestedMenuOption(filteredSections);
			setCurrentSelectedOption("");
		}, 500);
	};
	const handleBack = () => {
		setTimeout(() => {
			if (nestedMenuOption.length > 1 || currentSelectedOption) {
				setNestedMenuOption(filteredSections);
				setCurrentSelectedOption("");
				setMenuItemAnimationClass(
					`${txpMotionClasses.motionFadeIn} ${txpMotionClasses.motionPositionNavBackRight}`
				);
			}
		}, 200);
	};

	// Internal Search analytics (TXPE-27574) — Search Entry interactions.
	// Reuses the existing analytics framework (trackLink). The {{lob}} segment
	// of the linkId is the brand.
	const searchLinkPrefix = `${brand}_search`;
	// Strip everything except alphanumerics so free-typed terms produce a stable linkId.
	const sanitizeSearchTerm = (term = "") => term.replace(/[^a-zA-Z0-9]/g, "");
	// A "page transition" interaction navigates the guest to the results page.
	const trackSearchTransition = (interaction, term = "") =>
		trackLink({
			linkId: `${searchLinkPrefix}_${interaction}_${sanitizeSearchTerm(term)}`,
			pageTransitionLink: true,
		});

	const trackSearchInitiate = () =>
		trackLink({ linkId: `${searchLinkPrefix}_SearchInitiate` }, "internalSearchStart");
	const trackSearchClear = () => trackLink({ linkId: `${searchLinkPrefix}_Clear` });
	// Back Arrow: fired only from the dedicated back button in the SearchBar.
	const trackSearchBack = () => trackLink({ linkId: `${searchLinkPrefix}_Back` });
	const trackTypedSearch = (term) => trackSearchTransition("TypedSearch", term);
	// Autocomplete Search: guest clicked an autocomplete option.
	const trackAutocompleteSearch = (text) => trackSearchTransition("Autocomplete", text);
	// Suggested Search: guest clicked a suggested search option.
	const trackSuggestedSearch = (text) => trackSearchTransition("Suggested", text);

	const handleSearchBarToggle = () => {
		!searchBarToggle && scrollTopAndLeft();
		setSearchBarToggle((prevState) => !prevState);
	};

	useEffect(() => {
		if (backdropRef?.current) {
			if (searchBarToggle && !isMobileView && backdropRef) {
				backdropRef.current.style.height = `${620}px`;
				return;
			}

			if (
				(!searchBarToggle && !backdropExpanded && !hoveredSection) ||
				isProfileSection(hoveredSection)
			) {
				backdropRef.current.style.height = `${64}px`;
			}
		}
	}, [
		searchBarToggle,
		isMobileView,
		isMenuActive,
		backdropExpanded,
		hoveredSection,
	]);

	useEffect(() => {
		if (sectionsHovered && hoveredSection && searchBarToggle) {
			setSearchHidden(true);
		} else if (!sectionsHovered || !searchBarToggle) {
			setSearchHidden(false);
		}
		// When sectionsHovered is true but hoveredSection is null (between sections),
		// do nothing — keep current searchHidden state (stays hidden)
	}, [sectionsHovered, hoveredSection, searchBarToggle]);

	// Clear flyout when mouse leaves sections while search is open
	useEffect(() => {
		if (!sectionsHovered && searchBarToggle && hoveredSection) {
			clearTimeout(switchTimeoutRef.current);
			setHoveredSection(null);
		}
	}, [sectionsHovered, searchBarToggle, hoveredSection]);

	// Close dropdowns and nested menu when search opens, restore menu on close
	useEffect(() => {
		if (searchBarToggle) {
			clearTimeout(switchTimeoutRef.current);
			setHoveredSection(null);
			setBackdropExpanded(false);
			if (isMobileView) {
				toggleBodyClass(true, openSearchClass);
			}
			if (isMobileView && menuToggle) {
				menuWasOpenBeforeSearchRef.current = true;
				setAnimationClass(txpMotionClasses.motionFadeOut);
				setTimeout(() => {
					handleCloseMenu();
				}, 300);
			}
		} else if (
			!searchBarToggle &&
			isMobileView &&
			menuWasOpenBeforeSearchRef.current
		) {
			menuWasOpenBeforeSearchRef.current = false;
			document?.body.classList?.add("navigation-nav-open-txp");
			setMenuToggle(true);
			setTimeout(() => {
				setAnimationClass(txpMotionClasses.motionFadeIn);
			}, 200);
		} else if (!searchBarToggle && isMobileView) {
			toggleBodyClass(false, openSearchClass);
		}
		return () => {
			toggleBodyClass(false, openSearchClass);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchBarToggle]);

	// Focus search input when search bar opens or reappears after hiding
	useEffect(() => {
		if (!searchBarToggle) return;
		const input = document.querySelector(
			".syndicated-navigation__chrome__search__form__input"
		);
		if (searchHidden) return;
		if (input) {
			const timer = setTimeout(() => {
				input.focus();
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [searchBarToggle, searchHidden]);

	// Keep search input focused while search is open (prevent ONLY programmatic blur from web components)
	useEffect(() => {
		if (!searchBarToggle) return;
		const input = document.querySelector(
			".syndicated-navigation__chrome__search__form__input"
		);
		if (!input) return;
		const handleBlur = (e) => {
			if (e.relatedTarget !== null) return;

			const sectionIsOpen = document.querySelector(
				".syndicated-section--onhover"
			);
			if (sectionIsOpen) return;

			requestAnimationFrame(() => {
				if (
					document.activeElement !== input &&
					document.activeElement === document.body
				) {
					input.focus();
				}
			});
		};
		input.addEventListener("blur", handleBlur);
		return () => input.removeEventListener("blur", handleBlur);
	}, [searchBarToggle]);

	const handleKeydown = (e) => {
		if (e.key === "Escape") {
			removeHoverOfSection();
			setHoveredSection(null);
			setBackdropExpanded(false);
			setIsMenuActive(false);
		}
	};

	const filterSections = () => {
		if (showNavigation || showLogo) {
			const sections =
				isMobileView || store
					? navigation
					: navigation?.filter(
							(section) =>
								!brandConfig.sectionsInChromeBar?.includes(section.id)
					  );
			return brandConfig.discoveryAgentEnabled
				? filterDiscoveryAgentLinks(sections, showDaLinks)
				: sections;
		} else {
			return [];
		}
	};

	const renderSections = (filteredSections = []) => {
		let isLastSection = false;

		return (
			showSections &&
			filteredSections?.map((section, key) => {
				isLastSection = isMobileView
					? false
					: key === filteredSections?.length - 1;
				return section.id === "cart" ? (
					<Cart
						key={key}
						{...section}
						isMenuActive={isMenuActive}
						setIsMenuActive={setIsMenuActive}
						isMobileView={isMobileView}
						isLastSection={isLastSection}
						txp={true}
						setSearchBarToggle={setSearchBarToggle}
						searchBarToggle={searchBarToggle}
						isHeaderHovered={isHeaderHovered}
						isScrolling={isScrolling}
						setHoveredSection={handleSetHoveredSection}
					/>
				) : (
					<SectionTxp
						{...section}
						setCollapsedSection={setCollapsedSection}
						collapse={
							!isMobileView || (isMobileView && collapsedSection === section.id)
						}
						key={key}
						ref={(el) => (sectionRefs.current[section.id] = el)}
						isMenuActive={isMenuActive}
						setIsMenuActive={setIsMenuActive}
						store={store}
						brand={brand}
						isLastSection={isLastSection}
						handleFullClose={() => {
							handleCloseMenu();
							removeHoverOfSection();
							setHoveredSection(null);
							setBackdropExpanded(false);
						}}
						txp={true}
						backdropRef={backdropRef}
						headerRef={headerRef}
						hoveredSection={hoveredSection}
						sectionsHovered={sectionsHovered}
						setHoveredSection={handleSetHoveredSection}
						isHeaderHovered={isHeaderHovered}
						isScrolling={isScrolling}
						setSearchBarToggle={setSearchBarToggle}
						searchBarToggle={searchBarToggle}
					/>
				);
			})
		);
	};

	// Reset nav state and toggle body class on viewport change
	useEffect(() => {
		if (menuToggle || hoveredSection) {
			handleCloseMenu();
			removeHoverOfSection();
			setHoveredSection(null);
			setCollapsedSection("");
			// Reset to the DA-filtered sections so the mobile nested menu never
			// falls back to the raw, unfiltered navigation (which would show
			// Discovery Agent links that should be hidden).
			setNestedMenuOption(filteredSections);
			setCurrentSelectedOption("");
		}
		if (brandConfig.classToIndicateMobileView) {
			document?.body.classList[isMobileView ? "add" : "remove"](
				brandConfig.classToIndicateMobileView
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobileView]);

	useEffect(() => {
		if ((modal || openedOneIdModal) && menuToggle) {
			handleMenuToggle();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal, openedOneIdModal, menuToggle]);

	useEffect(() => {
		if (isMobileView && menuToggle) {
			const focusable =
				sectionNavRef?.current?.querySelector(focusableElements);
			setTimeout(() => {
				focusable?.focus();
			}, 200);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menuToggle]);

	// When the mouse leaves the header entirely, close any open section.
	// This complements the isHeaderHovered guard in Section's handleMouseLeave.
	useEffect(() => {
		const wasHovered = prevIsHeaderHoveredRef.current;
		prevIsHeaderHoveredRef.current = isHeaderHovered;

		if (wasHovered && !isHeaderHovered && !sectionsHovered && hoveredSection) {
			setIsMenuActive(false);
			setTimeout(() => setHoveredSection(null), 0);
			clearTimeout(switchTimeoutRef.current);
			toggleBodyClass(false, preventScrollTxpClass);
		}
	}, [isHeaderHovered, sectionsHovered, hoveredSection]);

	const filteredSections = filterSections();

	const isCompactRange = useCheckMediaQuery(compactLayoutMediaQuery);

	useEffect(() => {
		setForceCompactLayout(filteredSections?.length > 4 && isCompactRange);
	}, [filteredSections?.length, isCompactRange, setForceCompactLayout]);

	// Keep the mobile nested menu's top-level list in sync with the DA-filtered
	// sections. The initial nestedMenuOption is seeded with the raw navigation and
	// showDaLinks resolves asynchronously, so re-sync while the user is at the top
	// level (not inside a selected section) to avoid showing hidden DA links.
	useEffect(() => {
		if (!currentSelectedOption) {
			setNestedMenuOption(filteredSections);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showDaLinks]);

	usePreventScroll(searchBarToggle);

	useEffect(() => {
		const el = navRef.current;
		if (isMobileView || !el) return;

		const preventScroll = (e) => {
			if (!isHeaderHovered || e.target.closest("#syndicated-flyout-cart")) return;
			e.preventDefault();
		};

		el.addEventListener("wheel", preventScroll, { passive: false });
		el.addEventListener("touchmove", preventScroll, { passive: false });

		return () => {
			el.removeEventListener("wheel", preventScroll);
			el.removeEventListener("touchmove", preventScroll);
		};
	}, [isMobileView, isHeaderHovered]);

	const renderNavigationContent = () => {
		return showBackButton ? (
			<div
				className={`${navigationClasses} ${syndicatedNavigationNavClass}--with-back-button`}
			>
				{isMobileView ? (
					<Fragment>
						<BackButton isMobileView />
						<Logo onLoadCallback={onLogoLoadCallback} />
						{backButton && (
							<div style={{ width: "20px", margin: "0 17px" }}></div>
						)}
					</Fragment>
				) : (
					<Fragment>
						<Logo onLoadCallback={onLogoLoadCallback} />
						<BackButton />
					</Fragment>
				)}
			</div>
		) : (
			<Fragment>
				{(showNavigation || showLogo) && (
					<div className={navigationClasses}>
						{!isMobileView && <Logo onLoadCallback={onLogoLoadCallback} />}
						{showNavigation && (
							<nav
								role="navigation"
								id={navigationAppMenuLinksId}
								aria-label={chrome?.accessibilityTextNavigationLinks}
								inert={menuToggle ? "" : undefined}
							>
								<div
									ref={sectionNavRef}
									className={`${syndicatedNavigationNavClass}__sections ${
										menuToggle
											? `${syndicatedNavigationNavClass}__sections--open`
											: ""
									}`}
									role={
										excludeAriaRole || isMobileView || isMenuActive
											? null
											: "menubar"
									}
									onMouseEnter={handleSectionsMouseEnter}
									onMouseLeave={handleSectionsMouseLeave}
								>
									{!isMobileView && (
										<AccessibleText>
											{chrome?.accessibilityTextNavigationLinks}
										</AccessibleText>
									)}
									{isMobileView && <Profile isMobileView username />}
									{renderSections(filteredSections)}
									{isMobileView && !store && (
										<Fragment>
											<Profile reservation userStatusLinks showIcon />
											<ShowDisney />
											{chromeLinks &&
												brandConfig?.chromeLinks?.showOnMobile && (
													<ChromeLinks
														brand={brand}
														elements={chrome?.elements}
														mobileIcon={brandConfig.chromeLinks.mobileIcon}
														cdnAssetsUrl={cdnAssetsUrl}
													/>
												)}
										</Fragment>
									)}
								</div>
							</nav>
						)}

						<div
							inert={menuToggle ? "" : undefined}
							className="syndicated-navigation__right"
						>
							<RightNavigation
								isTxp={isTxp}
								store={store}
								searchBarToggle={searchBarToggle}
								chrome={chrome}
								syndicatedNavigationToggleClass={
									syndicatedNavigationToggleClass
								}
								brand={brand}
								showSearchBar={showSearchBar}
								handleSearchBarToggle={handleSearchBarToggle}
								isMobileView={isMobileView}
								extraOptions={extraOptions}
								handleCloseMenu={handleCloseMenu}
								isMenuActive={isMenuActive}
								setIsMenuActive={setIsMenuActive}
								sectionRefs={sectionRefs}
								collapsedSection={collapsedSection}
								setCollapsedSection={setCollapsedSection}
								hoveredSection={hoveredSection}
								isHeaderHovered={isHeaderHovered}
								isScrolling={isScrolling}
								setHoveredSection={handleSetHoveredSection}
								setSearchBarToggle={setSearchBarToggle}
								headerRef={headerRef}
								backdropRef={backdropRef}
								showCart={showSections && showNavigation}
							/>
						</div>

						<Fragment>
							{isMobileView && (
								<PageHeaderTxp
									brand={brand}
									chrome={chrome}
									currentSelectedOption={currentSelectedOption}
									handleBack={handleBack}
									handleCloseIcon={handleCloseIcon}
									handleMenuToggle={handleMenuToggle}
									handleSearchBarToggle={handleSearchBarToggle}
									hideMenuToggler={hideMenuToggler}
									isScrolling={isScrolling}
									menuToggle={menuToggle}
									navigationAppMenuLinksId={navigationAppMenuLinksId}
									onLogoLoadCallback={onLogoLoadCallback}
									searchBarToggle={searchBarToggle}
									showSearchBar={showSearchBar}
									store={store}
									syndicatedNavigationClass={syndicatedNavigationClass}
								/>
							)}
						</Fragment>
					</div>
				)}

				{isMobileView && (
					<Suspense fallback={null}>
						<NestedMenu
							setCurrentSelectedOption={setCurrentSelectedOption}
							currentSelectedOption={currentSelectedOption}
							nestedMenuOption={nestedMenuOption}
							setNestedMenuOption={setNestedMenuOption}
							fade={fade}
							setFade={setFade}
							isMobileView={isMobileView}
							extraOptions={extraOptions}
							menuToggle={menuToggle}
							animationClass={animationClass}
							setAnimationClass={setAnimationClass}
							menuItemAnimationClass={menuItemAnimationClass}
							setMenuItemAnimationClass={setMenuItemAnimationClass}
							onClose={handleCloseIcon}
						/>
					</Suspense>
				)}
				{searchBarToggle &&
					showNavigation &&
					showSearchBar &&
					showChromeItems && (
						<div
							inert={menuToggle && !searchBarToggle ? "" : undefined}
							style={
								searchHidden
									? {
											visibility: "hidden",
											opacity: 0,
											height: 0,
											overflow: "hidden",
											transition: "none",
									  }
									: {
											visibility: "visible",
											opacity: 1,
											height: "auto",
											overflow: "visible",
											transition: "opacity 200ms ease-in",
									  }
							}
						>
							<SearchBar
								searchAutocomplete={searchAutocomplete}
								shouldShowTxpSearchBar
								handleSearchBarToggle={handleSearchBarToggle}
								suggestedSearch={chrome?.suggestedSearch}
								AnalyticsWrapper={AnalyticsWrapper}
								onSearchInitiate={trackSearchInitiate}
								onClearSearch={trackSearchClear}
								onTypedSearch={trackTypedSearch}
								onBack={trackSearchBack}
								onAutocompleteSearch={trackAutocompleteSearch}
								onSuggestedSearch={trackSuggestedSearch}
							/>
						</div>
					)}
			</Fragment>
		);
	};

	return (
		<div
			data-theme="vista/parks-now"
			data-responsive-typography="false"
			className={
				syndicatedNavigationClass +
				(store ? ` ${syndicatedNavigationClass}--store tradeHeader` : "") +
				` ${
					logoLoaded
						? syndicatedNavigationClassShow
						: syndicatedNavigationClassHide
				}
        ` +
				syndicatedNavigationTxpClass
			}
			ref={navRef}
		>
			{!isMobileView && (
				<>
					{searchBarToggle && (
						<div
							className={`${syndicatedNavigationClass}__scrim`}
							onClick={handleOnScrimClick}
						></div>
					)}
					<div
						ref={backdropRef}
						className={`${syndicatedNavigationClass}__backdrop-shadow ${motionClasses}`}
					></div>
				</>
			)}
			<Container large={filteredSections?.length >= largeAmountOfSections}>
				{renderNavigationContent()}
			</Container>
		</div>
	);
};

export default NavigationTxp;
